import useGetMe from '@/api/queries/me/useGetMe';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import useGetMyBusinessHours from '@/api/queries/me/useGetMyBusinessHours';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetScheduledEvent from '@/api/queries/scheduleEvents/useGetScheduledEvent';
import taskKeys from '@/api/queries/tasks/keyFactory';
import useGetTask from '@/api/queries/tasks/useGetTask';
import { useUserHasPermission } from '@/hooks/usePermissions';
import CreateEditTaskSidebarControl from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebarControl';
import {
  useCreateTaskMutation,
  useUpdateTaskMutation,
} from '@/ui/components/createEditTaskSidebar/hook';
import { CreateEditTaskFormValues } from '@/ui/components/createEditTaskSidebar/types';
import { buildDefaultValues } from '@/ui/components/createEditTaskSidebar/utils';
import {
  AccessDeniedState,
  Fieldset,
  Sidebar,
  SkeletonLoader,
  SidebarProps as TSidebarProps,
} from '@payaca/ambrosia';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { addAccountBusinessDays } from '@payaca/shared-isomorphic/dates';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';

const DEFAULT_NUM_OF_DAYS_TO_DEADLINE = 3;

export type CreateEditTaskFormDefaultValues = Partial<CreateEditTaskFormValues>;

export type TProps = {
  taskId?: number;
  projectId?: number;
  scheduledEventId?: number;
  onSuccess?: (taskId: number) => void;
  defaultValues?: CreateEditTaskFormDefaultValues;
} & Omit<TSidebarProps, 'title'>;

const CreateEditTaskSidebar: FC<TProps> = (props) => {
  const {
    taskId,
    projectId,
    scheduledEventId,
    onClose,
    onSuccess,
    defaultValues,
    ...rest
  } = props;

  /**
   * state
   */
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const hasPermissionToPersistCustomFieldTask = useUserHasPermission({
    permissions: [TasksPermissions.PERSIST_CUSTOM_FIELD_TASK],
  });

  const hasPermissionToManageCustomFieldValues = useUserHasPermission({
    permissions: [AccountsPermissions.MANAGE_CUSTOM_FIELD_VALUES],
  });

  const canPersistCustomFieldTask =
    hasPermissionToPersistCustomFieldTask &&
    hasPermissionToManageCustomFieldValues;

  const hasLegacyFormTemplates = useMyAccountHasFeature(
    'hasLegacyFormTemplates'
  );

  /**
   * queries
   */
  const queryClient = useQueryClient();
  const { task } = useGetTask(taskId);
  const { data: me } = useGetMe();
  const { project } = useGetProjectByIdOverview(projectId?.toString());
  const { data: scheduledEvent } = useGetScheduledEvent(scheduledEventId);
  const { data: businessHours, isLoading: isLoadingBusinessHours } =
    useGetMyBusinessHours();

  /**
   * mutations
   */
  const createTaskMutation = useCreateTaskMutation();
  const updateTaskMutation = useUpdateTaskMutation();

  const isEditing = typeof taskId === 'number';

  const onSubmit = async (
    data: CreateEditTaskFormValues,
    originalData: CreateEditTaskFormValues
  ) => {
    setIsProcessing(true);

    try {
      let mutatedTaskId: string | undefined;
      if (isEditing) {
        // Editing
        mutatedTaskId = await updateTaskMutation(data, {
          taskPublicId: task?.publicId,
          originalData,
        });
      } else {
        // Create
        mutatedTaskId = await createTaskMutation(data, {
          scheduledEventPublicId: scheduledEvent?.scheduledEvent.publicId,
          projectPublicId: project?.publicId,
        });
      }

      if (project?.publicId) {
        await queryClient.invalidateQueries({
          queryKey: projectKeys.tasks(project?.publicId),
        });
      } else if (task?.project?.publicId) {
        await queryClient.invalidateQueries({
          queryKey: projectKeys.tasks(task?.project?.publicId),
        });
      }

      if (taskId) {
        await queryClient.invalidateQueries({
          queryKey: taskKeys.task(taskId),
        });
      }

      await queryClient.invalidateQueries({
        queryKey: taskKeys.tasks(),
      });

      if (mutatedTaskId) {
        onClose?.();
        onSuccess?.(parseInt(mutatedTaskId));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const defaultDueDate = businessHours
    ? addAccountBusinessDays(
        new Date(),
        DEFAULT_NUM_OF_DAYS_TO_DEADLINE,
        businessHours
      )
    : undefined;
  const parsedDefaultValues = buildDefaultValues(task, {
    dueDate: scheduledEvent
      ? new Date(scheduledEvent.scheduledEvent.endAt)
      : defaultDueDate,
    assignedTo:
      scheduledEvent && scheduledEvent.scheduledEvent.assignees.length > 0
        ? scheduledEvent.scheduledEvent.assignees[0]?.publicId
        : me?.user.publicId,
    ...defaultValues,
  });

  const shouldRenderForm = (() => {
    const conditions: boolean[] = [];

    conditions.push(!isLoadingBusinessHours);

    if (taskId) {
      conditions.push(!!task);
    }

    if (typeof projectId === 'number') {
      conditions.push(!!project);
    }

    if (typeof scheduledEventId === 'number') {
      conditions.push(!!scheduledEvent);
    }

    if (task && task.__typename === 'CustomFieldsTask') {
      conditions.push(canPersistCustomFieldTask);
    }

    return conditions.every(Boolean);
  })();

  return (
    <Sidebar
      title={taskId ? 'Edit Task' : 'Create Task'}
      onClose={onClose}
      warnUserOnClose={isDirty}
      warnUserContent={{
        body: 'The changes to this Task are not saved and will be lost.',
      }}
      {...rest}
    >
      {shouldRenderForm ? (
        <CreateEditTaskSidebarControl
          onSubmit={onSubmit}
          isProcessing={isProcessing}
          isEditing={isEditing}
          defaultValues={parsedDefaultValues}
          setIsDirty={setIsDirty}
          canCreateFormTask={hasLegacyFormTemplates}
          canCreateCustomFieldTask={
            typeof projectId === 'number' &&
            typeof scheduledEventId === 'number' &&
            canPersistCustomFieldTask
          }
        />
      ) : task &&
        task.__typename === 'CustomFieldsTask' &&
        !canPersistCustomFieldTask ? (
        <AccessDeniedState
          className="my-auto"
          text="You don't have permission to edit this Task"
        />
      ) : (
        <div className="p-4">
          <Fieldset>
            <SkeletonLoader.Input />
            <SkeletonLoader.Input />
            <SkeletonLoader.Input />
          </Fieldset>
        </div>
      )}
    </Sidebar>
  );
};

export default CreateEditTaskSidebar;
